import React, { useState, useEffect } from 'react';
import {
    TextField,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    Show,
    SimpleShowLayout,
    required,
    maxLength,
    EditButton,
    DateField,
    useNotify,
    useRefresh,
    Confirm
} from 'react-admin';
import { Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';
import { Card, CardContent, Typography, Box } from '@mui/material';

const DeletePromptTemplateButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/prompt_templates/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Prompt template deleted successfully', { type: 'success' });
            refresh();
            window.location.reload();
        } catch (error) {
            console.error('Error deleting prompt template:', error);
            notify('Failed to delete prompt template', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                color="error"
                startIcon={<DeleteIcon />}
            >
                Delete
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Prompt Template"
                content="Are you sure you want to delete this template? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

// Custom component to display template text with truncation
const TemplateField = ({ record, source }) => {
    // Handle loading state when record is undefined
    if (!record || !record[source]) {
        return (
            <Box sx={{ maxWidth: 300 }}>
                <Typography variant="body2" sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary'
                }}>
                    Loading...
                </Typography>
            </Box>
        );
    }

    const text = record[source];
    const truncated = text.length > 100 ? text.substring(0, 100) + '...' : text;

    return (
        <Box sx={{ maxWidth: 300 }}>
            <Typography variant="body2" sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.875rem'
            }}>
                {truncated}
            </Typography>
        </Box>
    );
};

// List component for prompt templates
export const PromptTemplateList = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/prompt_templates?end=1000`; // Fetch all for client-side sort
        httpClient(url)
            .then(({ json }) => {
                setTemplates(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching prompt templates:', error);
                setLoading(false);
            });
    }, []);

    const columns = [
        { key: 'name', header: 'Name' },
        {
            key: 'template',
            header: 'Template Preview',
            render: (record) => <TemplateField record={record} source="template" />
        },
        {
            key: 'created_at',
            header: 'Created',
            render: (record) => new Date(record.created_at).toLocaleString()
        },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="prompt_templates" />
                    <DeletePromptTemplateButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Prompt Templates</h2>
                <Link
                    to="/prompt_templates/create"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Create
                </Link>
            </div>
            <SortableTable data={templates} columns={columns} />
        </div>
    );
};

// Create component for prompt templates
export const PromptTemplateCreate = (props) => (
    <Create {...props} title="Create Prompt Template">
        <SimpleForm>
            <TextInput
                source="name"
                label="Template Name"
                validate={[required(), maxLength(255)]}
                fullWidth
            />
            <TextInput
                source="template"
                label="Template Content"
                validate={required()}
                multiline
                rows={10}
                fullWidth
                helperText="Enter the prompt template text. You can use placeholders like {variable_name} for dynamic content."
            />
        </SimpleForm>
    </Create>
);

// Edit component for prompt templates
export const PromptTemplateEdit = (props) => (
    <Edit {...props}>
        <SimpleForm>
            <TextInput
                source="name"
                label="Template Name"
                validate={required()}
                fullWidth
                helperText="Enter a descriptive name for this prompt template."
            />
            <TextInput
                source="template"
                label="Template Content"
                validate={required()}
                multiline
                rows={10}
                fullWidth
                helperText="Enter the prompt template text. You can use placeholders like {variable_name} for dynamic content."
            />
        </SimpleForm>
    </Edit>
);

// Show component for prompt templates
export const PromptTemplateShow = (props) => (
    <Show {...props} title="Prompt Template Details">
        <SimpleShowLayout>
            <TextField source="name" label="Template Name" />
            <DateField source="created_at" label="Created" showTime />
            <DateField source="updated_at" label="Last Updated" showTime />
            <Card sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Template Content
                    </Typography>
                    <Box sx={{
                        backgroundColor: '#f5f5f5',
                        p: 2,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        <TextField source="template" component="pre" />
                    </Box>
                </CardContent>
            </Card>
        </SimpleShowLayout>
    </Show>
);
